import { ref } from "vue";
import { authApi } from "@/api";
import type { Representante, UlemoLicence } from "@/api/session";

/** Modo de instalação do Ulemo ("ONLINE" ou "OFFLINE"), obtido sempre da API
 * (`PUT /api/auth-conta/licenca`, sem autenticação) — nunca fixado no Vue.
 * Fixar este valor no frontend permitia que os dois lados divergissem (ex.:
 * o Vue a mostrar "Criar conta" enquanto o backend recusava o pedido).
 *
 * Numa instalação OFFLINE, Integração de APIs, Configuração das chaves FE,
 * assinar/renovar licença e criar conta ficam indisponíveis.
 *
 * O router garante que `garantirModoInstalacaoCarregado()` é chamado (e tem
 * sucesso) antes de qualquer rota ser renderizada — ver router/index.ts. */
const ulemoLicence = ref<UlemoLicence | null>(null);
const representante = ref<Representante | null>(null);
// Falso por omissão (antes de carregar da API) — falha para o lado seguro: nunca mostrar "Criar conta" sem confirmação.
const estaEmProducao = ref(false);

let carregamentoEmCurso: Promise<boolean> | null = null;

export function useModoInstalacao() {
  return { ulemoLicence, representante, estaEmProducao };
}

/** Garante que o modo de instalação foi obtido da API. Devolve `false` se a
 * API não respondeu ou não devolveu um valor válido — nesse caso nenhuma
 * rota deve ficar acessível (ver router/index.ts). Chamável novamente para
 * repetir a tentativa (ex.: botão "Tentar novamente"). */
export async function garantirModoInstalacaoCarregado(): Promise<boolean> {
  if (ulemoLicence.value) return true;
  if (carregamentoEmCurso) return carregamentoEmCurso;

  carregamentoEmCurso = (async () => {
    try {
      const resposta = await authApi.licencaInstalacao();
      if (resposta?.ulemoLicence === "ONLINE" || resposta?.ulemoLicence === "OFFLINE") {
        ulemoLicence.value = resposta.ulemoLicence;
        representante.value = resposta.representante;
        estaEmProducao.value = resposta.estaEmProducao ?? false;
        return true;
      }
      return false;
    } catch {
      return false;
    } finally {
      carregamentoEmCurso = null;
    }
  })();

  return carregamentoEmCurso;
}
